import {
    Avatar,
    Box,
    EmptyState,
    formatDateWithoutTime,
    LinkText,
    Table,
    TextCell,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Column } from "react-table";

import GroupUsersTable from "./GroupUsersTable";
import style from "./user-groups.scss";
import UserGroupsKebabMenu from "./UserGroupsKebabMenu";
import GroupIcon from "components/icons/GroupIcon";
import { viewActionsContext } from "components/layout/ApplicationLayout";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import SearchView from "components/search/SearchView";
import { AUTH_USER_GROUP_DELETE, AUTH_USER_GROUP_EDIT } from "domain/authority";
import { TABLE_PAGE_LIMIT } from "domain/globalConstants";
import { GroupsTable, UserGroup } from "domain/userGroups";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userGroupsService } from "services/user-groups/UserGroupsService";
import { userSessionService } from "services/user/UserSessionService";
import buttons from "styles/buttons.scss";
import formStyle from "styles/form.scss";
import layoutStyle from "styles/layout.scss";
import { getInitials } from "utils/commonFunctions";
import { RepositoryKey } from "utils/repository";

import testIds from "testIds.json";

interface TableState {
    userGroupsData: UserGroup[];
    cursor: string;
    scrollPosition?: number;
}

export interface Props {
    count: number;
    initialGroupData: UserGroup;
    search?: string;
    onGroupDelete: () => void;
    onGroupEdit: () => void;
    refreshCount: number;
}

const UserGroupsTable = (props: Props) => {
    const { t } = useTranslation();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const viewActions = React.useContext(viewActionsContext);

    const [tableState, setTableState] = React.useState<TableState>({
        userGroupsData: [],
        cursor: "",
        scrollPosition: 0,
    });
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const [initialLoading, setInitialLoading] = React.useState<boolean>(true);
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [search, setSearch] = React.useState<string>("");
    const [userGroupVisibility, setUserGroupVisibility] = React.useState(false);
    const [activeUserGroupDetails, setActiveUserGroupDetails] = React.useState<UserGroup>(props.initialGroupData);

    const showUserGroup = (userGroup: UserGroup) => {
        setUserGroupVisibility(true);
        setActiveUserGroupDetails(userGroup);
    };

    const hideUserGroupDialogue = () => {
        setUserGroupVisibility(false);
    };

    const fetchData = (initialLoading: boolean) => {
        setLoading(true);
        setInitialLoading(initialLoading);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        userGroupsService
            .fetchGroups(abortController, initialLoading ? "" : tableState.cursor, undefined, search)
            .then((data: GroupsTable) => {
                setTableState((prevState) => ({
                    ...prevState,
                    scrollPosition: prevState.userGroupsData.length - 1,
                    userGroupsData: prevState.userGroupsData.concat(data.groups),
                    cursor: data.cursor,
                }));
                setLoading(false);
                setRequestFailureMessage("");
            })
            .catch(() => {
                if (!abortController?.signal.aborted) {
                    setRequestFailureMessage(t("UserGroups.table.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController?.signal.aborted) {
                    setLoading(false);
                    setInitialLoading(false);
                }
            });
    };

    const columns: Array<Column<UserGroup>> = [
        {
            Header: () => t("UserGroups.table.groupName"),
            accessor: "name",
            Cell: (cellInfo) => (
                <TextCell>
                    <Box>
                        {userSessionService.userHasAnyAuthority([AUTH_USER_GROUP_EDIT, AUTH_USER_GROUP_DELETE]) && (
                            <UserGroupsKebabMenu
                                groupUuid={cellInfo.cell.row.original.uuid}
                                name={cellInfo.cell.row.original.name}
                                onGroupDelete={props.onGroupDelete}
                                onGroupEdit={props.onGroupEdit}
                                groupTuple={cellInfo.cell.row.original}
                                tableRef={tableContainerRef}
                            />
                        )}
                        <Tooltip>
                            <TooltipTrigger>
                                <Box paddingLeft={"Md"}>
                                    <Avatar
                                        backgroundColor={cellInfo.row.original.backgroundColor}
                                        textColor={cellInfo.row.original.textColor}
                                        value={getInitials(cellInfo.value)}
                                        size={"Lg"}
                                    />
                                </Box>
                            </TooltipTrigger>
                            <TooltipContent>{cellInfo.value}</TooltipContent>
                        </Tooltip>
                        <Box marginLeft={"Xs"}>
                            <LinkText
                                as={"button"}
                                onClick={() => {
                                    showUserGroup(cellInfo.cell.row.original);
                                    usageStatisticsService.sendEvent({
                                        category: Category.USER_GROUP,
                                        action: Action.VIEW_USER_GROUP_DETAILS,
                                    });
                                }}
                            >
                                {cellInfo.value}
                            </LinkText>
                        </Box>
                    </Box>
                </TextCell>
            ),
            width: 190,
        },
        {
            Header: () => t("UserGroups.table.totalUserCount"),
            accessor: "totalUserCount",
            Cell: ({ cell: { value } }) => <TextCell>{value}</TextCell>,
            width: 120,
        },
        {
            Header: () => t("UserGroups.table.role"),
            accessor: "roleName",
            Cell: ({ cell: { value } }) => <TextCell>{value}</TextCell>,
            width: 150,
        },
        {
            Header: () => t("UserGroups.table.creationDate"),
            accessor: "created",
            Cell: ({ cell: { value } }) => <TextCell>{formatDateWithoutTime(value)}</TextCell>,
            width: 140,
        },
        {
            Header: () => t("UserGroups.table.edited"),

            accessor: "edited",
            Cell: ({ cell: { value } }) => <TextCell>{formatDateWithoutTime(value)}</TextCell>,
            width: 140,
        },
        {
            Header: () => t("UserGroups.table.creator"),
            accessor: "creatorName",
            Cell: ({ cell: { value } }) => <TextCell>{value}</TextCell>,
            width: 150,
        },
    ];

    React.useEffect(() => {
        setTableState({ userGroupsData: [], cursor: "", scrollPosition: 0 });
        fetchData(true);
    }, [props.count, search, props.refreshCount]);

    return (
        <>
            <div className={layoutStyle.aboveTable}>
                <div className={layoutStyle.recordCount}>
                    {tableState.userGroupsData.length > 0 && (
                        <div>{t("UserGroups.searchResultHint", { dataCount: tableState.userGroupsData.length })}</div>
                    )}
                </div>
                <div className={formStyle.search}>
                    <SearchView setSearch={setSearch} searchInProgress={false} />
                </div>
            </div>
            <div className={layoutStyle.tableWrapper}>
                <div className={layoutStyle.tableInnerWrapper}>
                    <Table
                        tableIdentity={RepositoryKey.USER_GROUPS_TABLE}
                        data={tableState.userGroupsData}
                        columns={columns}
                        loaded={!initialLoading}
                        failureMessage={requestFailureMessage}
                        scrollTo={tableState.scrollPosition}
                        emptyStateMessage={
                            <EmptyState text={t("UsersTable.emptyStateMessage")} viewActions={viewActions} />
                        }
                        testIds={{ itself: testIds.workArea.userGroup.table.itself }}
                        containerRef={tableContainerRef}
                    />
                </div>
            </div>
            <Modal
                key={1}
                isOpen={userGroupVisibility}
                hideModal={hideUserGroupDialogue}
                modalTitle={activeUserGroupDetails.name}
                titleIcon={
                    <div className={style.modelSvg}>
                        <GroupIcon
                            backgroundColor={activeUserGroupDetails.backgroundColor}
                            textColor={activeUserGroupDetails.textColor}
                            groupInitials={getInitials(activeUserGroupDetails.name)}
                        />
                    </div>
                }
            >
                <div className={style.modelWidth}>
                    <GroupUsersTable
                        groupUuid={activeUserGroupDetails.uuid}
                        hideUserGroupDialogue={hideUserGroupDialogue}
                        activeUserGroupDetails={activeUserGroupDetails}
                    />
                </div>
            </Modal>
            {tableState.cursor != null &&
                tableState.userGroupsData.length >= TABLE_PAGE_LIMIT &&
                tableState.userGroupsData.length != 0 &&
                requestFailureMessage === "" &&
                (loading ? (
                    <LoadingIndicator small={true} withTable />
                ) : (
                    <button
                        className={buttons.loadMoreButtonWithoutIcon}
                        onClick={() => {
                            fetchData(false);
                            usageStatisticsService.sendEvent({
                                action: Action.LOAD_MORE,
                                category: Category.USER_GROUP,
                            });
                        }}
                        data-testid={testIds.common.primaryView.table.loadMoreButton}
                    >
                        {t("Common.loadMore")}
                    </button>
                ))}
        </>
    );
};

export default UserGroupsTable;
